/**
 * When something happened, as the task screen says it (docs/roadmap-2026-10.md "Readable
 * timestamps on tap"). The meta line shows one short form; a tap on it shows the full form, the day
 * and time and then how long ago, and a second tap goes back. Both clients format through here
 * (src/format.ts and client/lib/format.ts re-export formatDateTime, formatRelative and formatAgo),
 * so the words never drift apart.
 */

const DAY_MS = 86_400_000;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * The day and time: "Sun, Oct 4, 8:13 PM", in the device's own zone. Built by hand rather than by
 * Intl, whose words differ by engine (the iPhone app's Hermes printed "Sun, Oct 4 at 8:13 PM" where
 * Chrome prints "Sun, Oct 4, 8:13 PM"), so both clients print the same words. The app is English.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${h % 12 || 12}:${m} ${h < 12 ? "AM" : "PM"}`;
}

/** "7 h ago", "in 3 d", "just now": the distance from now, in the largest whole unit. */
export function formatRelative(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "";
  const diff = new Date(iso).getTime() - now;
  const abs = Math.abs(diff);
  const min = 60_000;
  const hour = 60 * min;
  let text: string;
  if (abs < min) return "just now";
  else if (abs < hour) text = `${Math.round(abs / min)} min`;
  else if (abs < DAY_MS) text = `${Math.round(abs / hour)} h`;
  else text = `${Math.floor(abs / DAY_MS)} d`;
  return diff < 0 ? `${text} ago` : `in ${text}`;
}

/**
 * A moment that has happened (a task added, a note posted, an upgrade built): formatRelative for
 * the past only. A phone whose clock runs a little behind the server's sees a fresh timestamp in
 * its future, which must read "just now", never "in 1 min" (owner, 2026-09-30).
 */
export function formatAgo(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return "";
  return formatRelative(iso, Math.max(now, new Date(iso).getTime()));
}

/**
 * The three ways the task screen tells a time:
 *  - "ago": "7 h ago", an open task's line at rest;
 *  - "exact": "Tue, Oct 5, 5:12 PM", a done task's line at rest;
 *  - "full": "Tue, Oct 5, 5:12 PM · 3 d ago", either line after a tap.
 */
export type WhenMode = "ago" | "exact" | "full";

export function whenLabel(iso: string | null | undefined, now: number, mode: WhenMode): string {
  if (!iso) return "";
  if (mode === "ago") return formatAgo(iso, now);
  if (mode === "exact") return formatDateTime(iso);
  return `${formatDateTime(iso)} · ${formatAgo(iso, now)}`;
}

/** A no-break space: holds a unit (a time, an amount of gold) together on one line. */
export const NBSP = "\u00a0";
/** Holds a phrase together: every space becomes a no-break space. */
const glue = (phrase: string) => phrase.replace(/[ \u202f]/g, NBSP);

/**
 * whenLabel as the screen draws it: the time is one unbreakable unit. "Sun, Oct 4, 8:13 PM" and
 * "5 h ago" never break inside; the full form may break only after its own dot ("Sun, Oct 4,
 * 8:13 PM ·" over "5 h ago"), as the meta line's own dots do.
 */
export function whenText(iso: string | null | undefined, now: number, mode: WhenMode): string {
  if (!iso) return "";
  if (mode === "ago") return glue(formatAgo(iso, now));
  if (mode === "exact") return glue(formatDateTime(iso));
  return `${glue(formatDateTime(iso))}${NBSP}· ${glue(formatAgo(iso, now))}`;
}

/** The meta line's separator: a no-break space before the dot, so a line never starts with "·". */
export const META_DOT = `${NBSP}· `;

/** What a time button shows: its resting form, or the full one once tapped. */
export function whenShown(rest: Exclude<WhenMode, "full">, open: boolean): WhenMode {
  return open ? "full" : rest;
}

/**
 * The button's accessible name, which says what a tap does. An open task's "7 h ago" offers the
 * exact time and, opened, goes back to how long ago; a done task's exact time offers how long ago
 * and, opened, goes back to the exact time alone.
 */
export function whenToggleLabel(rest: Exclude<WhenMode, "full">, open: boolean): string {
  const toExact = rest === "ago" ? !open : open;
  return toExact ? "Show the exact time" : "Show how long ago";
}
